import { START_LOCATION } from "vue-router";

const RELOAD_KEY = "stale-build-reload";
const RELOAD_WINDOW_MS = 60000;
const POLL_INTERVAL_MS = 5 * 60000;
const VISIBLE_CHECK_MIN_MS = 60000;
const CHUNK_ERROR = /dynamically imported module|Importing a module script failed|Unable to preload CSS/;

function reloadOnce(url) {
  // Offline, a chunk error looks just like a 404; reloading would replace the page with the browser's offline error page.
  if (!navigator.onLine) return false;
  try {
    if (Date.now() - (Number(sessionStorage.getItem(RELOAD_KEY)) || 0) < RELOAD_WINDOW_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    return false;
  }
  // Same origin only: a route path starting with // would otherwise be read as another host.
  window.location.assign(location.origin + url);
  return true;
}

/**
 * @param {import("vue-router").Router} router
 * @returns {import("vue").FunctionPlugin}
 */
export function createStaleBuildGuard(router) {
  return function () {
    let stale = false;
    let lastCheck = 0;

    async function checkDeployedBuild() {
      lastCheck = Date.now();
      try {
        const res = await fetch(`${import.meta.env.BASE_URL}version.json`, { cache: "no-store" });
        const deployed = await res.json();
        // Newer only: during a rolling update a tab can reach the old container, and must not bounce back to it.
        if (Date.parse(deployed.builtAt) > Date.parse(__APP_BUILD__.builtAt)) {
          stale = true;
          clearInterval(poll);
        }
      } catch {
        // Offline or mid-deploy: the next check will tell.
      }
    }

    const poll = setInterval(checkDeployedBuild, POLL_INTERVAL_MS);
    checkDeployedBuild();
    document.addEventListener("visibilitychange", () => {
      if (!stale && document.visibilityState === "visible" && Date.now() - lastCheck > VISIBLE_CHECK_MIN_MS) checkDeployedBuild();
    });
    window.addEventListener("pageshow", (event) => {
      if (event.persisted && !stale) checkDeployedBuild();
    });

    router.beforeEach((to, from) => {
      // Left pending, not aborted: vue-router undoes an aborted back/forward with history.go(), which cancels the reload.
      if (stale && from !== START_LOCATION && to.path !== from.path && reloadOnce(router.resolve(to.fullPath).href)) return new Promise(() => {});
    });

    router.onError((error, to) => {
      if (CHUNK_ERROR.test(error?.message)) reloadOnce(router.resolve(to.fullPath).href);
    });
  };
}
